/**
 * SuccessSpine — close-out animation that mirrors the SplashIntro brand story.
 * Structure split into: orchestrator (this file) + SVG (SuccessSpineSvg) + styles (SuccessSpineStyles).
 * Each piece can be individually restyled or replaced.
 */
export default function SuccessSpine({ size = 160 }) {
  return (
    <div className="relative inline-block" data-testid="success-spine" style={{ width: size, height: size * (140 / 120) }}>
      <SuccessSpineSvg size={size} />
      <SuccessSpineStyles />
    </div>
  );
}

function SuccessSpineSvg({ size }) {
  return (
    <svg
      width={size}
      height={size * (140 / 120)}
      viewBox="0 0 120 140"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="ph-succ-svg"
    >
      <circle cx="60" cy="60" r="52" className="ph-succ-ring" />
      <circle cx="60" cy="14" r="10" className="ph-succ-head" />
      <path
        d="M60 24 C 58 40, 63 55, 60 70 C 57 85, 63 100, 60 118 L60 132"
        className="ph-succ-spine"
        strokeLinecap="round"
      />
      {[36, 48, 60, 72, 84, 96, 108].map((y, i) => (
        <line
          key={y}
          x1={i % 2 === 0 ? 48 : 50}
          y1={y}
          x2={i % 2 === 0 ? 72 : 70}
          y2={y}
          className={`ph-succ-vert ph-succ-vert-${i}`}
          strokeLinecap="round"
        />
      ))}
      <path
        d="M40 70 L54 84 L82 54"
        className="ph-succ-check"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SuccessSpineStyles() {
  return (
    <style>{`
      .ph-succ-svg { overflow: visible; }

      .ph-succ-head {
        fill: #2AB6E5;
        stroke: #1E4FA8;
        stroke-width: 2.5;
        transform-origin: 60px 14px;
        animation: phs-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.05s both,
                   phs-head-fade 0.5s ease-out 1.35s forwards;
      }
      .ph-succ-spine {
        stroke: #1F3B2B;
        stroke-width: 4.5;
        fill: none;
        stroke-dasharray: 240;
        stroke-dashoffset: 240;
        animation: phs-draw 0.85s cubic-bezier(0.65, 0, 0.35, 1) 0.15s forwards,
                   phs-fade 0.4s ease-out 1.35s forwards;
      }
      .ph-succ-vert {
        stroke: #6B8E5A;
        stroke-width: 3.5;
        opacity: 0;
        animation: phs-vert-in 0.28s ease-out forwards,
                   phs-fade 0.4s ease-out 1.35s forwards;
      }
      ${[0,1,2,3,4,5,6].map(i => `.ph-succ-vert-${i} { animation-delay: ${0.5 + i*0.05}s, 1.35s; }`).join("\n")}

      .ph-succ-check {
        stroke: #1F3B2B;
        stroke-width: 7;
        fill: none;
        stroke-dasharray: 100;
        stroke-dashoffset: 100;
        animation: phs-draw-check 0.55s cubic-bezier(0.65, 0, 0.35, 1) 1.55s forwards;
      }
      .ph-succ-ring {
        stroke: #6B8E5A;
        stroke-width: 3;
        fill: rgba(107, 142, 90, 0.08);
        transform-origin: 60px 60px;
        transform: scale(0);
        opacity: 0;
        animation: phs-ring-in 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 1.4s forwards,
                   phs-ring-pulse 2.4s ease-in-out 2.2s infinite;
      }

      @keyframes phs-pop { from { transform: scale(0); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      @keyframes phs-draw { to { stroke-dashoffset: 0; } }
      @keyframes phs-vert-in {
        from { opacity: 0; transform: scaleX(0.4); }
        to   { opacity: 1; transform: scaleX(1); }
      }
      @keyframes phs-fade { to { opacity: 0; } }
      @keyframes phs-head-fade { to { opacity: 0.15; transform: scale(0.9); } }
      @keyframes phs-draw-check { to { stroke-dashoffset: 0; } }
      @keyframes phs-ring-in { to { transform: scale(1); opacity: 1; } }
      @keyframes phs-ring-pulse {
        0%,100% { transform: scale(1); }
        50%     { transform: scale(1.06); }
      }

      @media (prefers-reduced-motion: reduce) {
        .ph-succ-head, .ph-succ-spine, .ph-succ-vert, .ph-succ-check, .ph-succ-ring {
          animation-duration: 0.01s !important;
          animation-delay: 0s !important;
        }
        .ph-succ-spine, .ph-succ-vert { opacity: 0; }
        .ph-succ-check { stroke-dashoffset: 0; }
        .ph-succ-ring { transform: scale(1); opacity: 1; }
      }
    `}</style>
  );
}
